import { useRef, useState } from "react";
import Select from "react-select";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Text } from "../Type";
import { OrganizationType } from "../../types/organizationType";
import { UserType } from "../../types/userType";
import Button from "../Button";
import EmptyCard from "../EmptyCard";
import SectionHeading from "../SectionHeading";
import TextInput from "../TextInput";
import OrganizationCard from "../OrganizationCard";
import Modal from "../Modal";
import TextLink from "../TextLinks";
import LocationCard from "../LocationCard";
import { formatAddress } from "../../utils/formatAddress";
import { phoneExtensionFieldOptions, phoneNumberFieldOptions, postalCodeFieldOptions, adpAuthRegNoOptions } from "../../utils/forms/validation";
import { clearIndicator, dropdownIndicator, indicatorSeparator, placeholder, valueContainer } from "../FilterDropdowns/commonStyles";
import styles from "./index.module.scss";

const GET_ORGANIZATIONS = gql`
  query Organizations {
    organizations {
      id
      name
      address {
        name
        address_1
        address_2
        business_phone
        business_phone_ext
        postal_code
        city
        province
        phone
      }
    }
  }
`;

const CREATE_ORGANIZATION = gql`
  mutation CreateOrganization($input: createOrganizationInput) {
    createOrganization(input: $input) {
      organization {
        id
        name
        address {
          address_1
          address_2
          business_phone
          business_phone_ext
          postal_code
          city
          province
          phone
        }
      }
    }
  }
`;

type PropsType = {
  methods: UseFormReturn;
  data: UserType;
  isAuth: boolean;
  isSelectModalOpen: boolean;
  setIsSelectModalOpen: (i: boolean) => void;
  isCreateModalOpen: boolean;
  setIsCreateModalOpen: (i: boolean) => void;
  org: OrganizationType;
  setOrg: (i: OrganizationType) => void;
};

const EditProfileForm = ({
  methods,
  data,
  isAuth,
  isSelectModalOpen,
  setIsSelectModalOpen,
  isCreateModalOpen,
  setIsCreateModalOpen,
  org,
  setOrg,
}: PropsType) => {
  const selectRef = useRef<Select | null>(null);
  const [, setBoundaryError] = useState<Error | null>(null);
  const { data: orgData, refetch: orgRefetch } = useQuery(GET_ORGANIZATIONS, {
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });
  const [createNewOrganization] = useMutation(CREATE_ORGANIZATION, {
    onCompleted: orgRefetch,
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  const handleSave = () => {
    if (selectRef.current) {
      const selectedOrg = selectRef.current.state.value?.data;
      setOrg(selectedOrg);
    }
    setIsSelectModalOpen(false);
  };

  const duplicateOrgExists = (name) => {
    for (let i = 0; i < orgData?.organizations?.length; i += 1) {
      if ((orgData?.organizations)[i].name === name) {
        return true;
      }
    }
    return false;
  };

  const handleCreate = (values) => {
    const sanitizedData = {
      name: values.name_of_organization,
      address: {
        address_1: values.address_1,
        address_2: values.address_2,
        business_phone: values.org_business_phone,
        business_phone_ext: values.org_business_phone_ext,
        city: values.organization_city,
        phone: values.org_phone,
        postal_code: values.postal_code,
        province: "ON",
        name: "",
        fax: "",
        lot: "",
        street_name: "",
        street_number: "",
        unit_number: "",
      },
    };
    const newOrgData = createNewOrganization({
      variables: {
        input: {
          data: sanitizedData,
        },
      },
    });
    newOrgData
      .then((res) => {
        setOrg({
          id: res?.data?.createOrganization.organization.id,
          ...sanitizedData,
        });
      })
      .catch((err) => {
        // eslint-disable-next-line
        console.log(err);
      });
    setIsCreateModalOpen(false);
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      display: "inline-flex",
      background: "white",
      width: "100%",
      maxHeight: "150px",
      padding: "0 10px",
      border: state.isFocused
        ? `1px solid ${styles.grey_400}`
        : `1px solid ${styles.grey_200}`,
      boxShadow: state.isFocused
        ? `0px 0px 0px 3px ${styles.blue_200}`
        : `0px 0px 0px 3px transparent`,
      borderRadius: "4px",
      "&:focus, &:hover": {
        borderColor: styles.grey_400,
      },
    }),
    clearIndicator,
    dropdownIndicator,
    indicatorSeparator,
    menu: (provided) => ({
      ...provided,
      maxHeight: "150px",
      borderRadius: "4px",
      boxShadow: "0px 1px 3px 0px #3F3F4426, 0px 0px 0px 1px #3F3F440D",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "0",
      maxHeight: "150px",
    }),
    placeholder,
    valueContainer,
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: styles.grey_100,
      borderRadius: "4px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      padding: "2px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      transition: "color 150ms ease-in-out",
      ":hover": {
        backgroundColor: styles.grey_100,
        color: styles.red_300,
      },
    }),
  };
  return (
    <FormProvider {...methods}>
      <form>
        <EmptyCard noPad>
          <SectionHeading title="Contact Information" />
          <div className={styles.sectionGrid}>
            <div className={styles.phoneCol}>
              <TextInput
                id="phone"
                label="* Phone Number"
                defaultValue={data.phone}
                placeholder="000-000-0000"
                fieldOptions={{
                  ...phoneNumberFieldOptions,
                  required: "Enter your phone number",
                }}
              />
            </div>
            <div className={styles.extCol}>
              <TextInput
                id="phone_ext"
                label="Ext"
                placeholder="000"
                fieldOptions={phoneExtensionFieldOptions}
                defaultValue={data.phone_ext}
              />
            </div>
          </div>
          {/* TODO: Administrators don't have either organization or location */}
          <SectionHeading
            title={isAuth ? "Professional Organization" : "Motion Location"}
          />
          <div className={styles.section}>
            <div className={styles.modalStyle}>
              <Modal
                isOpen={isSelectModalOpen}
                title="Select Organization"
                onClose={() => setIsSelectModalOpen(false)}
              >
                <Text size="large" className={styles.headingText}>
                  Choose organization
                </Text>
                <div className={styles.select}>
                  <Select
                    ref={selectRef}
                    styles={customStyles}
                    isSearchable
                    placeholder="Search by name of organization"
                    maxMenuHeight="197px"
                    options={orgData?.organizations.map((o) => ({
                      value: o.id,
                      label: `${o.name} (${formatAddress(o.address)})`,
                      data: o,
                    }))}
                    noOptionsMessage={() => (
                      <TextLink
                        onClick={() => {
                          setIsSelectModalOpen(false);
                          setIsCreateModalOpen(true);
                        }}
                      >
                        + Create an organization
                      </TextLink>
                    )}
                  />
                </div>
                <div className={styles.modalButton}>
                  <Button primary onClick={handleSave}>
                    Select Organization
                  </Button>
                </div>
              </Modal>
            </div>
            <div className={styles.modalStyle}>
              <Modal
                isOpen={isCreateModalOpen}
                title="Create Organization"
                onClose={() => setIsCreateModalOpen(false)}
              >
                <Text size="small" className={styles.headingText}>
                  You will not be able to edit this organization after you
                  create it, and it will become available for other Authorizers
                  to select. Please provide as much information as possible and
                  check it for accuracy.
                </Text>
                <TextInput
                  id="name_of_organization"
                  label="* Name of Organization"
                  fieldOptions={{
                    validate: {
                      isValidOrg: (value: string) =>
                        duplicateOrgExists(value)
                          ? "Organization with this name already exists."
                          : undefined,
                    },
                    required: "This is a required field.",
                  }}
                />
                <TextInput
                  id="address_1"
                  label="* Address Line 1"
                  fieldOptions={{
                    required: "This is a required field.",
                  }}
                />
                <TextInput id="address_2" label="Address Line 2" />
                <TextInput
                  id="postal_code"
                  label="* Postal Code"
                  placeholder="A1A1A1"
                  fieldOptions={{
                    ...postalCodeFieldOptions,
                    required: "This is a required field.",
                  }}
                />
                <TextInput
                  id="organization_city"
                  label="* City"
                  fieldOptions={{
                    required: "This is a required field.",
                  }}
                />
                <TextInput id="province" label="Province" value="ON" />
                <TextInput
                  id="org_phone"
                  label="Phone"
                  placeholder="000-000-0000"
                  fieldOptions={phoneNumberFieldOptions}
                />
                <div className={styles.grid6038}>
                  <TextInput
                    id="org_business_phone"
                    label="Business Phone"
                    placeholder="000-000-0000"
                    fieldOptions={phoneNumberFieldOptions}
                  />
                  <TextInput
                    id="org_business_phone_ext"
                    label="Extension"
                    placeholder="000"
                    fieldOptions={phoneExtensionFieldOptions}
                  />
                </div>
                <div className={styles.modalButton}>
                  <Button primary onClick={methods.handleSubmit(handleCreate)}>
                    Create Organization
                  </Button>
                </div>
              </Modal>
            </div>
            {!isAuth ? (
              <div>
                {data.locations.map((loc, i) => (
                  <div key={i.toString()} className={styles.locationCardMargin}>
                    <LocationCard key={loc.id} location={loc} />
                  </div>
                ))}
              </div>
            ) : (
              <OrganizationCard
                organization={org || data.organization}
                isAuth={isAuth}
                editable
                onClick={() => {
                  setIsSelectModalOpen(true);
                }}
              />
            )}
            {isAuth && (
              <TextInput
                id="adp_auth_num"
                label="ADP Authorizer Number"
                defaultValue={data.adp_auth_num}
                className={styles.adpField}
                fieldOptions={adpAuthRegNoOptions}
              />
            )}
          </div>
        </EmptyCard>
      </form>
    </FormProvider>
  );
};

export default EditProfileForm;
